import { inject, Injectable, signal } from '@angular/core';
import { ApiHandlerService } from './api-base.service';
import { SessionService } from './session.service';
import { map, Subject } from 'rxjs';
import { ReportTemplate } from '../interfaces';
import { Observable } from 'rxjs';

@Injectable()
export class TemplateService {
  apiHandlerService = inject(ApiHandlerService);
  sessionService = inject(SessionService);
  template = signal<ReportTemplate | null | undefined>(undefined);

  private templateChanged = new Subject<ReportTemplate | null>();

  templatesList = signal<ReportTemplate[] | undefined>(undefined);

  saveTemplate(
    templateName: string | undefined | null,
    templateDescription: string | undefined | null,
    params: any,
    reportId: number | undefined,
    templateId?: string | undefined | null,
    isTemporary = true
  ): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'save_report_template',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId || null,
        p_template_id: templateId || null,
        p_is_temporary: isTemporary,
        p_template_desc: templateDescription || null,
        p_template_name: templateName || null,
        p_input_params: params,
      },
    };

    return this.apiHandlerService.Post(url, body).pipe(
      map((resp: any) => {
        if (resp?.error_message) {
          throw new Error(resp.error_message);
        }
        return resp as ReportTemplate;
      })
    );
  }

  getTemplateById(
    reportId: number,
    templateId: string
  ): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_report_templates',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId,
        p_template_id: templateId,
        p_is_temporary: false,
      },
    };

    return this.apiHandlerService
      .Post<any, ReportTemplate[] | any>(url, body)
      .pipe(
        map((response) => {
          const template = response[0];
          if (
            template &&
            (template.errorCode || template.successful === 'false')
          ) {
            throw new Error(template.message);
          }
          this.template.set(template || null);
          return template;
        })
      );
  }

  getAllTemplates(reportId: number | undefined): Observable<ReportTemplate[]> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_report_templates',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId || null,
        p_template_id: null,
      },
    };

    return this.apiHandlerService.Post(url, body);
  }

  deleteTemplate(templateId: string): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'delete_report_template',
      'ce'
    );

    const body = {
      p_report_params: {
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_template_id: templateId,
      },
    };

    return this.apiHandlerService.Post(url, body);
  }

  setUrlTemplateId(templateId: string): void {
    const url = new URL(window.location.href);
    if (url.searchParams.get('templateId') !== templateId) {
      url.searchParams.set('templateId', templateId);
      window.history.pushState({}, '', url.toString());
    }
  }

  get templateChangedSubject() {
    return this.templateChanged;
  }
}
